import { FC } from 'react';
import dayjs from 'dayjs';

import ConfirmationDialog from 'components/ConfirmationDialog/ConfirmationDialog';
import { RemovedPlans } from 'modules/effectiveDate/utils/dateChange';
import { LONG_MONTH_DATE_FORMAT } from 'utils/dateUtil';

import styles from './ChangeEffectiveDateDialog.module.less';

type Props = {
  visible: boolean;
  // New date as YYYY-MM-DD.
  date: string;
  removed: RemovedPlans[];
  onOk: () => void;
  onCancel: () => void;
};

const joinLabels = (labels: string[]) =>
  labels.length > 1
    ? `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`
    : labels[0];

const ChangeEffectiveDateDialog: FC<Props> = ({
  visible,
  date,
  removed,
  onOk,
  onCancel,
}) => {
  const refilled = removed.filter(
    (line) => line.refilled && !line.partiallyEmptied && !line.partiallyRefilled
  );
  const partiallyRefilled = removed.filter(
    (line) => line.refilled && (line.partiallyEmptied || line.partiallyRefilled)
  );
  const left = removed.filter(
    (line) => line.emptied && !line.refilled && !line.partiallyEmptied
  );
  const partiallyLeft = removed.filter(
    (line) =>
      line.emptied &&
      ((!line.refilled && line.partiallyEmptied) || line.partiallyRefilled)
  );

  return (
    <ConfirmationDialog
      visible={visible}
      onOk={onOk}
      onCancel={onCancel}
      title="Change Effective Date"
      okText="Yes - Change Effective Date"
      cancelText="Cancel"
      flipFooterOrder
      isCancelLink
      width="560px"
    >
      <p>
        These plans are not active on{' '}
        {dayjs(date).format(LONG_MONTH_DATE_FORMAT)} and will be removed from
        the microsite:
      </p>
      <div className={styles.removedList}>
        <table className={styles.removedTable}>
          <tbody>
            {removed.map((line) => (
              <tr key={line.section}>
                <td>{line.label}</td>
                <td>{line.plans.join(', ')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p>
        {refilled.length > 0 &&
          ` ${joinLabels(
            refilled.map((line) => line.label)
          )} will be left with no plans, so the default plans for the new date are added.`}
        {left.length > 0 &&
          ` ${joinLabels(
            left.map((line) => line.label)
          )} will be left with no plans.`}
        {partiallyRefilled.length > 0 &&
          ` Default plans for the new date are added to empty ${joinLabels(
            partiallyRefilled.map((line) => line.label)
          )} benefit types.`}
        {partiallyLeft.length > 0 &&
          ` Some ${joinLabels(
            partiallyLeft.map((line) => line.label)
          )} benefit types will be left with no plans.`}
      </p>
    </ConfirmationDialog>
  );
};

export default ChangeEffectiveDateDialog;
